import clsx from "clsx";
import Button from "components/Button";
import Input from "components/Input";
import React, { useEffect, useState } from "react";
import DeleteAccountModal from "./DeleteAccountModal";
import { useAppSelector } from "store";
import userService from "api/user/user.service";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useValidationSchema } from "i18n/yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { IconEyeOff, IconEyeOn, IconWarning } from "icons";
import { useDispatch } from "react-redux";
import { setSnackbar, setUser } from "store/commonSlice";
import InputUpload from "components/InputUpload";

const userDetailSchema = yup
  .object({
    name: yup.string(),
    work: yup.string(),
  })
  .required();

const AccountSettings = () => {
  const { user } = useAppSelector((state) => state.common);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const passwordSchema = useValidationSchema((tFn) =>
    yup
      .object({
        currentPassword: yup.string().required(tFn("validation:account.current_password_required")),
        newPassword: yup.string().required(tFn("validation:auth.password_required")),
        confirmNewPassword: yup
          .string()
          .required(tFn("validation:auth.password_required"))
          .test("passwords-match", tFn("validation:auth.passwords_mismatch"), function (value) {
            return this.parent.newPassword === value;
          }),
      })
      .required()
  );

  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [uploadedPhoto, setUploadedPhoto] = useState("");
  // Distinguishes "no new upload" ("") from "user cleared the existing photo" — the latter must
  // persist an empty photo, which "" alone can't express against a stored profilePhoto.
  const [photoRemoved, setPhotoRemoved] = useState(false);

  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    formState: { errors: passwordErrors },
  } = useForm({
    resolver: yupResolver(passwordSchema),
  });

  const {
    register: reqisterUserDetail,
    handleSubmit: handleSubmitUserDetail,
    // formState: { errors: userDetailErrors },
    setValue,
  } = useForm({
    resolver: yupResolver(userDetailSchema),
    defaultValues: { name: user.name ?? "" },
  });

  useEffect(() => {
    setValue("name", user.name);
  }, [user]);

  const onPasswordChangeValid = (data: any) => {
    const currentPassword = data.currentPassword;
    const newPassoword = data.newPassword;
    const newPasswordConfirm = data.confirmNewPassword;

    if (user.email && user.id) {
      userService
        .changePassword(user.id, {
          email: user.email,
          current_password: currentPassword,
          password: newPassoword,
          password_replay: newPasswordConfirm,
        })
        .then(() => {
          dispatch(
            setSnackbar({
              icon: "",
              message: t("account:password.toasts.success"),
            })
          );
        })
        .catch(() => {
          dispatch(
            setSnackbar({
              icon: IconWarning,
              message: t("account:errors.generic"),
            })
          );
        });
    }
  };
  const onChangePasswordSubmit = () => {
    // No invalid-form callback: logging RHF errors would dump the password inputs' refs to the console,
    // which reaches Sentry and Replay. RHF still renders the messages (REF-6708).
    handleSubmitPassword(onPasswordChangeValid)();
  };

  const onEditUserDetailValid = (data: any) => {
    const name = data.name ?? "";
    // `null` clears the photo on the BE (an empty string is treated as "no change"); otherwise the
    // freshly-uploaded URL, falling back to the existing one. Redux keeps the plain-string form.
    const nextProfilePhoto: string | null = photoRemoved ? null : uploadedPhoto || user.profilePhoto || "";
    const nextProfilePhotoStr = nextProfilePhoto ?? "";
    const currentPhoto = user.profilePhoto ?? "";

    // Nothing changed — skip the request.
    if (name === (user.name ?? "") && nextProfilePhotoStr === currentPhoto) return;

    userService
      .editUserDetail(user.id, { name: name, profile_photo: nextProfilePhoto })
      .then(() => {
        dispatch(setUser({ ...user, name: name, profilePhoto: nextProfilePhotoStr }));
        setUploadedPhoto(nextProfilePhotoStr);
        setPhotoRemoved(false);
        dispatch(
          setSnackbar({
            icon: "",
            message: t("account:profile.toasts.success"),
          })
        );
      })
      .catch(() => {
        dispatch(
          setSnackbar({
            icon: IconWarning,
            message: t("account:errors.generic"),
          })
        );
      });
  };
  const onEditUserDetailSubmit = () => {
    // No invalid-form callback: logging RHF errors would dump the profile inputs' refs (name, email) to the
    // console, which reaches Sentry and Replay. RHF still renders the messages (REF-6708).
    handleSubmitUserDetail(onEditUserDetailValid)();
  };

  // What the avatar shows right now: nothing if cleared, else the pending upload or the stored photo.
  const previewPhoto = photoRemoved ? "" : uploadedPhoto || user.profilePhoto;

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex flex-col rounded-md bg-black-300 border border-black-300 max-w-[386px]">
        <div className="p-2 bg-body-100 rounded-t-md">
          <h6 className="text-h6 text-white">{t("account:profile.title")}</h6>
        </div>
        <div className="flex flex-col p-2.5 gap-5 bg-black">
          <div className="flex items-center gap-2.5">
            <div className="flex-center rounded-full bg-primary w-16 h-16 text-h3 text-primary-foreground uppercase overflow-hidden">
              {previewPhoto ? <img src={previewPhoto} alt={t("common:sidebar.profile_alt")} className="w-full h-full object-cover" /> : user?.name?.[0]}
            </div>
            <InputUpload
              callback={(url: string) => {
                setUploadedPhoto(url);
                setPhotoRemoved(false);
              }}
            >
              <Button className="btn-primary h-9 px-2">{t("account:profile.upload_picture")}</Button>
            </InputUpload>
            {previewPhoto && (
              <Button
                className="btn-secondary h-9 px-2"
                onClick={() => {
                  setUploadedPhoto("");
                  setPhotoRemoved(true);
                }}
              >
                {t("account:profile.remove_picture")}
              </Button>
            )}
          </div>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-2.5">
              <span className="text-h7 text-white">{t("account:profile.email_label")}</span>
              <Input disabled placeholder={t("account:profile.placeholder")} defaultValue={user?.email} />
            </div>
            <div className="flex flex-col gap-2.5">
              <span className="text-h7 text-white">{t("account:profile.fullname_label")}</span>
              <Input placeholder={t("account:profile.fullname_placeholder")} {...reqisterUserDetail("name")} defaultValue={user?.name} />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2.5 p-2.5 bg-black-100 rounded-b-md">
          <Button className="btn-primary h-9 px-2" onClick={onEditUserDetailSubmit}>
            {t("account:profile.save_changes")}
          </Button>
        </div>
      </div>
      <div className="flex flex-col rounded-md bg-black-300 border border-black-300 max-w-[386px]">
        <div className={clsx("p-2 cursor-pointer bg-body-100", changePasswordOpen ? "rounded-t-md" : "rounded-md")} onClick={() => setChangePasswordOpen((prev) => !prev)}>
          <h6 className={clsx("text-h6 w-fit", !changePasswordOpen ? "text-purple dark:text-green underline" : "text-white")}>{t("account:password.title")}</h6>
        </div>
        {changePasswordOpen && (
          <div className={clsx("flex flex-col gap-5 bg-black rounded-b-md")}>
            <div className="flex flex-col p-2.5">
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-2.5">
                  <span className="text-h7 text-white">{t("account:password.current_label")}</span>
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder={t("auth:new_password.password_placeholder")}
                    error={passwordErrors.currentPassword?.message}
                    {...registerPassword("currentPassword")}
                    icon={
                      !showPassword ? (
                        <IconEyeOn className="text-grey hover:text-white cursor-pointer" onClick={() => setShowPassword(true)} />
                      ) : (
                        <IconEyeOff className="text-white hover:text-grey cursor-pointer" onClick={() => setShowPassword(false)} />
                      )
                    }
                  />
                </div>
                <div className="flex flex-col gap-2.5">
                  <span className="text-h7 text-white">{t("account:password.new_label")}</span>
                  <Input
                    type={showNewPassword ? "text" : "password"}
                    placeholder={t("auth:new_password.confirm_password_placeholder")}
                    error={passwordErrors.newPassword?.message}
                    {...registerPassword("newPassword")}
                    icon={
                      !showNewPassword ? (
                        <IconEyeOn className="text-grey hover:text-white cursor-pointer" onClick={() => setShowNewPassword(true)} />
                      ) : (
                        <IconEyeOff className="text-white hover:text-grey cursor-pointer" onClick={() => setShowNewPassword(false)} />
                      )
                    }
                  />
                </div>
                <div className="flex flex-col gap-2.5">
                  <span className="text-h7 text-white">{t("account:password.confirm_label")}</span>
                  <Input
                    type={showConfirmNewPassword ? "text" : "password"}
                    placeholder={t("auth:new_password.confirm_password_placeholder")}
                    error={passwordErrors.confirmNewPassword?.message}
                    {...registerPassword("confirmNewPassword")}
                    icon={
                      !showConfirmNewPassword ? (
                        <IconEyeOn className="text-grey hover:text-white cursor-pointer" onClick={() => setShowConfirmNewPassword(true)} />
                      ) : (
                        <IconEyeOff className="text-white hover:text-grey cursor-pointer" onClick={() => setShowConfirmNewPassword(false)} />
                      )
                    }
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2.5 p-2.5 bg-black-100 rounded-b-md">
              <Button className="btn-primary h-9 px-2" onClick={onChangePasswordSubmit}>
                {t("account:password.submit")}
              </Button>
            </div>
          </div>
        )}
      </div>
      {/* <div className="flex flex-col rounded-md bg-black-300 border border-black-300 max-w-[386px] p-2">
        <h6 className="cursor-pointer text-h6 text-orange underline w-fit" onClick={() => setShowDeleteAccountModal(true)}>
          Delete Account
        </h6>
      </div>
      <DeleteAccountModal show={showDeleteAccountModal} onClose={() => setShowDeleteAccountModal(false)} /> */}
    </div>
  );
};

export default AccountSettings;
